/**
 * GKレジデンス702号室の簡易再現CG。
 * 既存の間取り図、室内写真、登録済み視点を基に、ロフト付き1Rとして構成する。
 * 正式な寸法図がない箇所は写真内の家具寸法から概算している。
 */

const H = 4.8;
const LOFT_Y = 2.55;
const wall = (from, to, openings = [], h = H, y = 0, mat = 'plaster') => ({ from, to, h, y, openings, mat });
const door = (a, w = 0.78, top = 2.05) => ({ a, w, top });
const win = (a, w, sill = 0.85, top = 2.05, fill = 'glass') => ({ a, w, sill, top, fill });

export default {
  id: 'gk702',
  ambient: 1.45,
  sun: 0.8,

  rooms: [
    // 1階: 玄関からLDKへ続くロフト付きワンルーム。
    { x: [-2.5, 3.2], z: [-1.8, 4.2], floor: 'oakLight', ceil: 'plaster', h: H, roof: 'roof', ceilThick: 0.18 },
    { x: [1.25, 3.12], z: [-1.72, 0.25], y: 0.01, thick: 0.02, floor: 'tileLight' },
    { x: [1.25, 3.12], z: [0.32, 2.2], y: 0.01, thick: 0.02, floor: 'tileLight' },
    { x: [1.25, 3.12], z: [2.27, 4.12], y: 0.01, thick: 0.02, floor: 'tileBeige' },

    // リビング奥の中二階ロフト。
    { x: [-2.42, 0.75], z: [-1.72, 0.65], y: LOFT_Y, thick: 0.22, floor: 'walnut', under: 'woodCeiling' },
    // 階段を上りきった所の踊り場。これが無いと階段とロフトが繋がらず、ロフトに入れない
    { x: [0.05, 0.78], z: [0.6, 1.62], y: LOFT_Y, thick: 0.22, floor: 'walnut', under: 'woodCeiling' },
  ],

  walls: [
    wall([-2.5, -1.8], [3.2, -1.8], [win(0.55, 1.8, 1.0, 2.05), win(3.45, 1.15, 1.0, 2.05)]),
    wall([-2.5, -1.8], [-2.5, 4.2], [win(3.75, 1.75, 0.72, 2.15)]),
    wall([3.2, -1.8], [3.2, 4.2], [win(3.0, 1.15, 1.0, 2.0, 'frosted')]),
    wall([-2.5, 4.2], [3.2, 4.2], [door(4.55, 0.9)]),

    // 右側に水回りとキッチンをまとめる。
    wall([1.2, -1.8], [1.2, 2.25], [door(0.45, 0.76), door(2.48, 0.76)]),
    wall([1.2, 0.28], [3.2, 0.28], [door(0.18, 0.74)]),
    wall([1.2, 2.25], [3.2, 2.25], [door(0.18, 0.78)]),

    // ロフト上の低い腰壁。吹き抜け側は視界を残す。踊り場から入る所だけ開ける
    wall([-2.5, 0.65], [0.75, 0.65], [{ a: 2.5, w: 0.75, top: 0.88, frame: false }], 0.88, LOFT_Y),
  ],

  stairs: [
    // 踏面が浅すぎると人が上れないので、踊り場の手前まで伸ばして1段あたり約16cmにする
    { x0: -1.8, x1: 0.1, z0: 0.75, z1: 1.55, y0: 0, y1: LOFT_Y, steps: 12, railZ: 0.78 },
  ],

  furniture: [
    // 写真と物件情報にある主要家具。
    { type: 'sofa', x: -1.42, z: 2.35, rot: 90 },
    { type: 'table', x: -0.2, z: 2.45, w: 1.0, d: 0.55, h: 0.38 },
    { type: 'table', x: 0.15, z: 3.55, w: 1.25, d: 0.72, h: 0.72 },
    { type: 'chair', x: -0.45, z: 3.55, rot: 90 },
    { type: 'chair', x: 0.75, z: 3.55, rot: 270 },
    { type: 'box', x: -2.18, z: 1.25, w: 0.42, h: 0.58, d: 1.35, mat: 'walnut' },
    { type: 'picture', x: -2.42, y: 0.88, z: 1.25, w: 0.92, h: 0.55, color: 0x15171a, rot: 90 },

    // ロフトの寝具。
    { type: 'bed', x: -1.45, y: LOFT_Y + 0.02, z: -0.72, w: 1.2, d: 1.95, low: true },
    { type: 'bed', x: -0.05, y: LOFT_Y + 0.02, z: -0.72, w: 0.95, d: 1.9, low: true },

    // キッチン・水回りの固定設備。
    { type: 'kitchen', x: 2.58, z: 3.15, len: 1.65, rot: 90 },
    { type: 'box', x: 1.58, z: 3.32, w: 0.62, h: 1.65, d: 0.62, color: 0xf0f0ed },
    { type: 'basin', x: 1.72, z: 1.62, w: 0.76, d: 0.46 },
    { type: 'mirror', x: 1.72, y: 1.18, z: 1.38, w: 0.58, h: 0.72 },
    { type: 'washer', x: 2.62, z: 1.55 },
    { type: 'toilet', x: 1.78, z: -0.72, rot: 90 },
    { type: 'bathtub', x: 2.58, z: -0.72, w: 1.1, d: 0.68, rot: 90 },
    { type: 'ac', x: -1.0, y: 2.1, z: 4.08, rot: 180 },
  ],

  lights: [
    { x: -0.8, y: 2.2, z: 2.3, intensity: 5.5, distance: 6 },
    { x: 0.45, y: 2.2, z: 3.45, intensity: 4.5, distance: 5 },
    { x: 2.25, y: 2.1, z: 3.1, intensity: 3.2, distance: 4 },
    { x: 2.2, y: 2.1, z: 1.25, intensity: 3.0, distance: 3 },
    { x: -0.9, y: 4.3, z: -0.55, intensity: 4.0, distance: 4 },
  ],
};
